import Reflux from 'reflux';
import _ from 'underscore';
import socket from 'shared/lib/socket';
import storeStateHelper from 'flux/stores/storeStateHelper';
import UserActions from 'flux/actions/UserActions';
import RemoteTeleprompterActions from 'flux/actions/cuesheet.fullscreen/RemoteTeleprompterActions';
import FullScreenCuesheetActions from 'flux/actions/cuesheet.fullscreen/FullScreenCuesheetActions';
import {
  AutoScrollEnabledDefault,
  AutoScrollSpeedDefaultIndex,
  FeaturedCapitalizationDefault,
  FontWeightDefault,
  LastScrollToIndexDefault,
  LineHeightPercentageDefaultIndex,
  LineHeightPercentages,
  PrompterDisplayClocks,
  PrompterItemInfoEnabledDefault,
  PrompterSingleItemEnabledDefault,
  PrompterRealtimeSubscriptionsEnabledDefault,
  PrompterTextMirrorDefault,
  PrompterTextVerticalMirrorDefault,
  PrompterWidthDefaultIndex,
  ReadArrowTopStyleDefault,
  ScrollToIndexDefault,
  ZoomPercentageDefaultIndex,
  ZoomPercentages,
} from 'shared/constants/FeaturedEditorConstants';
import { ViewRelativePaths } from 'shared/constants/CuesheetViews';
import {
  isRemoteTeleprompter,
  isTeleprompter,
} from 'main/components/show/cuesheet/cuesheetViewHelper';
const { RemoteTeleprompter: RemoteTeleprompterPath } = ViewRelativePaths;

let lastOperatorScrollTimestamp = 0;

/**
 * Prompter Presentation Store
 *
 */
export default Reflux.createStore({
  ...storeStateHelper,

  listenables: [RemoteTeleprompterActions],

  _initState() {
    return {
      autoScrollSpeedIndex: AutoScrollSpeedDefaultIndex,
      autoScrollEnabled: AutoScrollEnabledDefault,
      displayClocks: PrompterDisplayClocks,
      lineHeight: LineHeightPercentages[LineHeightPercentageDefaultIndex],
      lineHeightIndex: LineHeightPercentageDefaultIndex,
      readArrowTop: ReadArrowTopStyleDefault,
      featuredCapitalization: FeaturedCapitalizationDefault,
      fontWeight: FontWeightDefault,
      lastScrollToIndex: LastScrollToIndexDefault,
      prompterItemInfoEnabled: PrompterItemInfoEnabledDefault,
      prompterWidthIndex: PrompterWidthDefaultIndex,
      prompterSingleItemEnabled: PrompterSingleItemEnabledDefault,
      prompterRealTimeEditsEnabled: PrompterRealtimeSubscriptionsEnabledDefault,
      prompterTextMirror: PrompterTextMirrorDefault,
      prompterTextVerticalMirror: PrompterTextVerticalMirrorDefault,
      scrollTop: 0,
      scrollToIndex: ScrollToIndexDefault,
      zoom: ZoomPercentages[ZoomPercentageDefaultIndex],
      zoomIndex: ZoomPercentageDefaultIndex,
      changedRows: {},
    };
  },

  init() {
    this.listenTo(UserActions.setClientId, '_onSetClientId');
    this.listenTo(FullScreenCuesheetActions.populate, 'onFullScreenPopulate');
    this.listenTo(
      FullScreenCuesheetActions.populateItemSetSubset,
      'onFullScreenPopulate',
    );
    // this logic was causing remote prompter's scroll position to get out of sync
    // commenting out for now
    this.listenTo(FullScreenCuesheetActions.refreshAllRows, 'onRefreshAllRows');
    this.setState(this._initState());

    this._socket = socket.get();

    if (isRemoteTeleprompter()) {
      this._socket.on('remoteTeleprompterSettingsChanged', (settings) => {
        const updateEditorDimensions =
          settings.prompterWidthIndex !== this.getInState('prompterWidthIndex');
        const { prompterRealTimeEditsEnabled } = settings;
        if (
          prompterRealTimeEditsEnabled !==
          this.getInState('prompterRealTimeEditsEnabled')
        ) {
          FullScreenCuesheetActions.toggleRealTime(
            prompterRealTimeEditsEnabled,
          );
          //only refresh rows if prompterRealTimeEditsEnabled is true to fetch any data
          //that may have been missed while it was disabled
          if (prompterRealTimeEditsEnabled) {
            FullScreenCuesheetActions.refreshAllRows();
          }
        }
        this.setStateAndTrigger(Object.assign(this.getState(), settings));
        if (updateEditorDimensions) {
          FullScreenCuesheetActions.updateEditorDimensions();
        }
      });
      // this logic was causing remote prompter's scroll position to get out of sync
      // commenting out for now
      this._socket.on('refreshedAllRows', () => {
        FullScreenCuesheetActions.refreshAllRows();
      });
      this._socket.on('rowCacheUpdated', (cache) => {
        return this.setStateAndTrigger(
          Object.assign(this.getState(), { cache }),
        );
      });
      this._socket.on('scrollPositionUpdated', (scrollPosition) => {
        const { scrollTop, timestamp } = scrollPosition;
        if (scrollTop || scrollTop === 0) {
          if (timestamp >= lastOperatorScrollTimestamp) {
            lastOperatorScrollTimestamp = timestamp;
            return RemoteTeleprompterActions.updateOperatorScrollTop(
              scrollTop,
              timestamp,
            );
          }
        }
      });
    } else {
      this._socket.on('requestTeleprompterSettings', () => {
        this._emitOperatorSettings();
      });
    }
  },

  onRefreshAllRows() {
    if (isTeleprompter()) {
      const state = this.getState();
      const { eventId, showId } = state;
      socket.get().emit('refreshAllRows', { eventId, showId });
    }
  },

  onFullScreenPopulate(teamId, eventId, showId) {
    this.mergeState({ teamId, eventId, showId });
  },

  onEmitOperatorSettings(settings) {
    this._emitOperatorSettings(settings);
  },

  onEmitScrollPosition(scrollTop) {
    const state = this.getState();
    const { teamId, eventId, showId } = state;
    socket.get().emit('scrollPositionUpdate', {
      teamId,
      eventId,
      showId,
      scrollPosition: {
        scrollTop,
        timestamp: Date.now(),
      },
    });
  },

  onEmitCache(cache) {
    const state = this.getState();
    const { teamId, eventId, showId } = state;
    socket.get().emit('rowCacheUpdate', {
      cache,
      teamId,
      eventId,
      showId,
    });
  },

  _emitOperatorSettings(settings) {
    let nextState = this.getState();
    if (settings) {
      nextState = Object.assign(nextState, settings);
    }
    if (window.location.href.indexOf(RemoteTeleprompterPath) === -1) {
      this.setState(nextState);
      if (!nextState.eventId) return;
      // these settings are to be controlled by the remote user, not the operator
      nextState = _.omit(nextState, ['prompterTextMirror', 'displayClocks']);
      socket.get().emit('remoteTeleprompterSettingsChange', nextState);
    }
  },

  _onSetClientId(clientId) {
    if (!isRemoteTeleprompter()) {
      const nextState = this.getState();
      nextState.operatorClientId = clientId;
      this.setState(nextState);
    }
  },

  onToggleSetting(setting) {
    const nextState = this.getState();
    const currentSetting = nextState[setting];
    nextState[setting] = !currentSetting;
    this.setStateAndTrigger(nextState);
  },
});
